import type { PrioritizationMethod } from '../../types/prioritization-selector';

export const prioritizationMethods: PrioritizationMethod[] = [
  {
    id: 'impact-effort-2x2',
    name: 'Impact vs Effort Matrix',
    description: 'A simple 2×2 matrix plotting impact against effort to quickly categorize items',
    category: 'strategic',
    complexity: 'simple',
    timeRequired: 'quick',
    teamSize: 'individual',
    useCases: ['Quick triage', 'Daily task prioritization', 'Solo decision making'],
    pros: ['Fast and intuitive', 'Visual and easy to understand', 'Good for quick decisions'],
    cons: ['Subjective scoring', 'Limited criteria', 'Not suitable for complex decisions'],
    whenToUse: ['Small item sets', 'Quick decisions', 'Solo prioritization'],
    whenNotToUse: ['Complex multi-criteria decisions', 'Large teams', 'High-stakes decisions'],
    steps: [
      'List all items to prioritize',
      'Rate each item on Impact (High/Medium/Low)',
      'Rate each item on Effort (High/Medium/Low)',
      'Plot items on 2×2 matrix',
      'Focus on High Impact, Low Effort items first'
    ],
    tools: ['Whiteboard', 'Sticky notes', 'Spreadsheet'],
    examples: ['Daily task triage', 'Feature prioritization', 'Bug prioritization'],
    relatedMethods: ['MoSCoW', 'Eisenhower Matrix', 'Value vs Effort'],
    scoringType: 'qualitative',
    skillRequired: 'any',
    facilitationTime: '15–30 min',
  },
  {
    id: 'moscow',
    name: 'MoSCoW Method',
    description: 'Categorizes items into Must have, Should have, Could have, and Won\'t have',
    category: 'operational',
    complexity: 'moderate',
    timeRequired: 'moderate',
    teamSize: 'small',
    useCases: ['Sprint planning', 'Feature prioritization', 'Team backlog management'],
    pros: ['Clear categorization', 'Stakeholder alignment', 'Easy to communicate'],
    cons: ['Can be subjective', 'May lead to scope creep', 'Requires consensus'],
    whenToUse: ['Team decisions', 'Sprint planning', 'Feature prioritization'],
    whenNotToUse: ['Solo decisions', 'Complex scoring needed', 'Large item sets'],
    steps: [
      'List all items to prioritize',
      'Categorize each item as Must have, Should have, Could have, or Won\'t have',
      'Review and adjust categorizations with team',
      'Focus on Must have items first',
      'Plan Should have items for next iteration'
    ],
    tools: ['Kanban board', 'Sticky notes', 'Project management software'],
    examples: ['Sprint planning', 'Feature roadmap', 'Product backlog'],
    relatedMethods: ['RICE', 'Value vs Effort', 'Kano Model'],
    scoringType: 'qualitative',
    skillRequired: 'any',
    facilitationTime: '30–60 min',
  },
  {
    id: 'rice',
    name: 'RICE Scoring',
    description: 'Scores items based on Reach, Impact, Confidence, and Effort',
    category: 'strategic',
    complexity: 'moderate',
    timeRequired: 'moderate',
    teamSize: 'small',
    useCases: ['Feature prioritization', 'Product roadmap', 'Team backlog management'],
    pros: ['Quantitative scoring', 'Considers multiple factors', 'Good for product decisions'],
    cons: ['Requires data', 'Can be time-consuming', 'May be subjective'],
    whenToUse: ['Product decisions', 'Feature prioritization', 'Team planning'],
    whenNotToUse: ['Quick decisions', 'Solo work', 'Very small item sets'],
    steps: [
      'Define Reach (how many people affected)',
      'Define Impact (how much impact per person)',
      'Define Confidence (how confident are you)',
      'Define Effort (how much work required)',
      'Calculate RICE score = (Reach × Impact × Confidence) / Effort',
      'Rank by RICE score'
    ],
    tools: ['Spreadsheet', 'RICE calculator', 'Project management software'],
    examples: ['Feature prioritization', 'Product roadmap', 'Team backlog'],
    relatedMethods: ['WSJF', 'Value vs Effort', 'Weighted Scoring'],
    scoringType: 'light-scoring',
    skillRequired: 'intermediate',
    facilitationTime: '1–2 hrs',
  },
  {
    id: 'wsjf',
    name: 'Weighted Shortest Job First (WSJF)',
    description: 'Prioritizes based on Cost of Delay divided by Job Size',
    category: 'strategic',
    complexity: 'complex',
    timeRequired: 'extensive',
    teamSize: 'large',
    useCases: ['Portfolio prioritization', 'Large team planning', 'Strategic decisions'],
    pros: ['Considers time value', 'Good for large teams', 'Strategic alignment'],
    cons: ['Complex to implement', 'Requires training', 'Time-consuming'],
    whenToUse: ['Portfolio management', 'Large teams', 'Strategic planning'],
    whenNotToUse: ['Quick decisions', 'Small teams', 'Simple tasks'],
    steps: [
      'Calculate Cost of Delay for each item',
      'Estimate Job Size for each item',
      'Calculate WSJF = Cost of Delay / Job Size',
      'Rank by WSJF score',
      'Review and adjust based on dependencies'
    ],
    tools: ['WSJF calculator', 'Portfolio management software', 'Spreadsheet'],
    examples: ['Portfolio prioritization', 'Strategic planning', 'Large team coordination'],
    relatedMethods: ['RICE', 'Cost of Delay', 'Value vs Effort'],
    scoringType: 'quantitative',
    skillRequired: 'advanced',
    facilitationTime: '2–4 hrs',
  },
  {
    id: 'eisenhower',
    name: 'Eisenhower Matrix',
    description: 'Categorizes tasks by urgency and importance',
    category: 'operational',
    complexity: 'simple',
    timeRequired: 'quick',
    teamSize: 'individual',
    useCases: ['Personal productivity', 'Task management', 'Time management'],
    pros: ['Simple and intuitive', 'Good for time management', 'Easy to implement'],
    cons: ['Limited criteria', 'May be subjective', 'Not suitable for complex decisions'],
    whenToUse: ['Personal productivity', 'Task management', 'Quick decisions'],
    whenNotToUse: ['Team decisions', 'Complex scoring', 'Strategic planning'],
    steps: [
      'List all tasks',
      'Categorize as Urgent/Important, Not Urgent/Important, Urgent/Not Important, or Not Urgent/Not Important',
      'Focus on Urgent/Important tasks first',
      'Schedule Important/Not Urgent tasks',
      'Delegate or eliminate other tasks'
    ],
    tools: ['Task management app', 'Whiteboard', 'Notebook'],
    examples: ['Daily task management', 'Personal productivity', 'Time blocking'],
    relatedMethods: ['Impact vs Effort', 'MoSCoW', 'Time Blocking'],
    scoringType: 'qualitative',
    skillRequired: 'any',
    facilitationTime: '15–30 min',
  }
];

export const getMethodById = (id: string): PrioritizationMethod | undefined => {
  return prioritizationMethods.find(method => method.id === id);
};

export const getMethodsByIds = (ids: string[]): PrioritizationMethod[] => {
  return ids.map(id => getMethodById(id)).filter(Boolean) as PrioritizationMethod[];
};

// Map bundles to recommended methods - using only methods that are defined above
export const bundleMethodMap: { [bundleId: string]: string[] } = {
  'B1': ['impact-effort-2x2', 'eisenhower'],
  'B2': ['impact-effort-2x2', 'eisenhower'],
  'B3': ['moscow', 'rice'],
  'B4': ['moscow', 'rice'],
  'B5': ['wsjf', 'rice'],
  'B6': ['rice', 'moscow'],
  'B7': ['wsjf'],
  'B8': ['wsjf', 'rice'],
  'B9': ['moscow', 'rice']
};

